<template>
  <div class="dropDownSearch" :class="[variant]">
    <span v-if="label" class="label">
      {{ label }}
      <span v-if="isOptionalLabel" class="optional-label">
        {{ optionalLabel }}
      </span>
    </span>
    <v-select
      :options="userlist"
      :filterable="false"
      :value="isEmpty(userValue) ? '' : userValue"
      label="userName"
      :placeholder="placeholder"
      :class="!disabled ? 'dropdown-select' : 'dropdown-select-disabled'"
      :disabled="disabled"
      @search="onSearch"
      @input="setSelected">
      <template #search="{ attributes, events }">
        <input
          class="dropdown-select vs__search"
          v-bind="attributes"
          v-on="events" />
      </template>
      <template #selected-option-container="{ option }">
        <div v-tooltip="tooltipText(option)" class="vs__selected">
          {{ option.userName }}
        </div>
      </template>
      <template #no-options="{ searching }">
        <template v-if="searching">
          <em class="dropdown-no-options">No results found</em>
        </template>
        <em v-else class="dropdown-no-options">No Options</em>
      </template>
    </v-select>
    <img v-if="icon" class="icon" :src="`assets/images/icons/${icon}.svg`" />
    <span v-if="hasError" class="error">{{ errorMessage }}</span>
  </div>
</template>

<script>
// Importing Vue and Vue Select
import Vue from "vue";
import vSelect from "vue-select";

// Importing the Vue Select Styles
import "vue-select/dist/vue-select.css";

// Utilities
import AxiosFetch from "@/utils/axios";
import { debounce, isEmpty, getMsalIdToken } from "@/utils/helpers";

// Registering the Vue Select Component
Vue.component("VSelect", vSelect);

// Importing the User Actions
import { userActions } from "@/actions";

// Constants
import { azureActiveDirectoryGroups } from "@/config/constant";

export default {
  name: "DropdownSearch",
  props: {
    value: {
      type: Object,
      default() {
        return {};
      },
    },
    label: {
      type: String,
      required: false,
      default: "",
    },
    placeholder: {
      type: String,
      required: false,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: "",
    },
    errorMessage: {
      type: String,
      default: "",
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      default: "",
    },
    fromAssigneeForm: {
      type: Boolean,
      default: false,
    },
    isOptionalLabel: {
      type: Boolean,
      default: false,
    },
    optionalLabel: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      userlist: [],
      userValue: this.value,
    };
  },
  mounted() {
    this.userValue = this.value;
    if (this.value?.apmId) {
      let employeeId = this.value?.apmId;
      this.userValue = "Loading...";
      this.onSearch(employeeId, () => false, true);
    }
  },
  methods: {
    onSearch(search, loading, fromInitialLoad) {
      if (search && search.length >= 3) {
        loading(true);
        this.search(loading, search, this, fromInitialLoad);
      }
    },
    search: debounce(async (loading, search, vm, fromInitialLoad) => {
      try {
        // delete AxiosFetch.defaults.headers.common["Ocp-Apim-Subscription-Key"];
        const opts = userActions.getUserListFromAzureAdByEmployeeId(
          search,
          azureActiveDirectoryGroups.azureAdForAssigneeDisputeAccess,
          `Bearer ${await getMsalIdToken(vm)}`
        );
        const { data } = await AxiosFetch(opts);
        let value = data.users;
        vm.userlist = value;
        if (vm.userValue) {
          vm.setSelected(value[0], fromInitialLoad);
          vm.$emit("submitState", { value: false });
        }
        loading(false);
      } catch (e) {
        console.error("Request failed");
        vm.userlist = "";
        vm.userValue = "Unable to fetch...";
      }
    }, 350),
    setSelected(value, fromInitialLoad) {
      this.userValue = value;
      this.$emit("handleFilterChange", {
        value: value,
        fromInitialLoad,
      });
    },
    tooltipText(item) {
      return item?.userName || "";
    },
    isEmpty,
  },
};
</script>

<style lang="scss" scoped>
@import "@/styles/imports";

.dropDownSearch {
  display: flex;
  flex-direction: column;
  position: relative;

  &.small {
    width: 368px;
    @include media("<=500px") {
      width: 280px;
    }
  }
  & .label {
    font-size: 16px;
    font-family: "Maersk Text";
    color: var(--Gray-800);
    margin-bottom: 5px;
    text-transform: none;
  }
  & input {
    font-family: "Maersk Text";
    font-size: 16px;
    background: transparent;
  }
  & .error {
    margin-bottom: 3px;
    color: red;
    font-size: 12px;
  }
  .dropdown-select,
  .dropdown-select-disabled {
    .vs__search::placeholder {
      color: var(--Gray-800);
    }

    .vs__open-indicator {
      &:hover {
        cursor: pointer;
      }
    }
  }
  .vs__search,
  .vs__search:focus {
    color: var(--primary-text) !important;
  }
  .vs__dropdown-toggle {
    border: 1px solid var(--Gray-400);
    height: 42px !important;
  }
  .dropdown-select {
    .vs__selected {
      color: var(---Gray-800);
    }
  }
  .dropdown-select-disabled {
    .vs__selected {
      color: black;
    }
  }
}
</style>
